import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { GameCard } from '../components/GameCard';
import { Helmet } from 'react-helmet-async';
import { HiHeart, HiSparkles, HiArrowNarrowLeft } from 'react-icons/hi';

interface Game {
  id: string;
  slug: string;
  title: string;
  description: string;
  image_icon: string;
  download_url: string;
  status: 'in_progress' | 'released' | 'coming_soon';
  is_visible: boolean;
  content_blocks: Array<{
    type: 'image' | 'video' | 'text';
    src?: string;
    alt?: string;
    caption?: string;
    content?: string;
  }>;
}

export const MyGames: React.FC = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFavoriteGames = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) {
          setIsLoading(false);
          return;
        }

        const { data: favorites, error: favoritesError } = await supabase
          .from('favorites')
          .select('game_id')
          .eq('user_id', session.user.id);

        if (favoritesError) throw favoritesError;

        if (favorites && favorites.length > 0) {
          const gameIds = favorites.map((f) => f.game_id);
          const { data: gamesData, error: gamesError } = await supabase
            .from('games')
            .select('*')
            .in('id', gameIds)
            .eq('is_visible', true);

          if (gamesError) throw gamesError;
          setGames(gamesData || []);
        }
      } catch (error) {
        console.error('Error fetching favorite games:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFavoriteGames();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen w-full bg-black flex items-center justify-center pt-28">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
          <span className="text-gray-400 text-sm">در حال بارگذاری بازی‌های ذخیره‌شده...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>بازی‌های من | Active Legend - مجموعه شخصی بازی‌ها</title>
        <meta
          name="description"
          content="مجموعه شخصی بازی‌های مورد علاقه شما در Active Legend. مدیریت و دسترسی آسان به بازی‌های محبوبتان."
        />
        <meta
          name="keywords"
          content="بازی‌های من, علاقه‌مندی‌ها, مجموعه شخصی, Active Legend, بازی‌های محبوب"
        />
        <link rel="canonical" href="https://activelegend.ir/my-games" />
        <meta property="og:title" content="بازی‌های من | Active Legend" />
        <meta
          property="og:description"
          content="مجموعه شخصی بازی‌های مورد علاقه شما در Active Legend"
        />
        <meta property="og:url" content="https://activelegend.ir/my-games" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="بازی‌های من | Active Legend" />
        <meta
          name="twitter:description"
          content="مجموعه شخصی بازی‌های مورد علاقه شما در Active Legend"
        />
      </Helmet>

      {/* Full-width container to prevent background bleed on wide screens */}
      <div className="min-h-screen w-full bg-black pt-32 md:pt-40 pb-20 overflow-x-hidden" dir="rtl">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-zinc-900 border border-white/10 text-white text-xs font-bold mb-4">
              <HiHeart className="w-4 h-4 text-rose-500 fill-rose-500" />
              <span>مجموعه بازی‌های ذخیره‌شده</span>
              {games.length > 0 && (
                <span className="bg-primary text-black px-2 py-0.2 rounded-full text-[11px] font-black">
                  {games.length}
                </span>
              )}
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-3 tracking-tight">
              🎮 بازی‌های مورد علاقه من
            </h1>

            <p className="text-gray-400 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
              عناوینی که به لیست علاقه‌مندی‌های خود اضافه کرده‌اید تا به سادگی به آپدیت‌ها و لینک‌های دانلود دسترسی داشته باشید.
            </p>
          </motion.div>

          {/* Games Grid or Empty State */}
          {games.length > 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            >
              {games.map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="max-w-md mx-auto text-center bg-zinc-900/60 border border-white/10 rounded-3xl p-8 sm:p-10 backdrop-blur-md shadow-2xl mt-4"
            >
              <div className="w-20 h-20 rounded-3xl bg-rose-500/10 border border-rose-500/20 text-rose-400 flex items-center justify-center mx-auto mb-6">
                <HiHeart className="w-10 h-10" />
              </div>

              <h3 className="text-xl font-bold text-white mb-2">هنوز بازی‌ای اضافه نکرده‌اید</h3>
              <p className="text-gray-400 text-xs sm:text-sm leading-relaxed mb-6">
                با مراجعه به صفحه بازی‌ها و کلیک روی آیکون قلب، بازی‌های محبوب خود را در این بخش نگهداری کنید.
              </p>

              <Link
                to="/games"
                className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-black font-extrabold px-6 py-3 rounded-2xl shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all text-sm w-full sm:w-auto"
              >
                <HiSparkles className="w-4 h-4" />
                <span>مشاهده و افزودن بازی‌ها</span>
                <HiArrowNarrowLeft className="w-4 h-4" />
              </Link>
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
};
